import { api } from "@/lib/apiClient";
import { useRoles } from "@/lib/useRoles";
import type { RoleDefinition } from "@/server/types";
import { nanoid } from "nanoid";
import { useCallback, useEffect, useRef, useState } from "react";
import { RoleForm } from "./RoleForm";

type SaveStatus = "idle" | "saving" | "saved";

const makeDefaultRole = (): RoleDefinition => ({
  id: `role-${nanoid(6)}`,
  label: "New Role",
  description: "",
  defaultModel: "sonnet",
  usePlanMode: false,
  prompt: "{{context}}",
});

type Props = { onClose: () => void };

export const RolesEditorModal = ({ onClose }: Props) => {
  const { data: fetchedRoles, isLoading: loading } = useRoles();
  const [roles, setRoles] = useState<RoleDefinition[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>("idle");
  const initializedRef = useRef(false);

  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fadeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingRolesRef = useRef<RoleDefinition[] | null>(null);

  const selectedRole = roles.find((r) => r.id === selectedId) ?? null;

  // Initialize local state from fetched roles (once)
  useEffect(() => {
    if (fetchedRoles && !initializedRef.current) {
      initializedRef.current = true;
      setRoles(fetchedRoles);
      if (fetchedRoles.length > 0) setSelectedId(fetchedRoles[0].id);
    }
  }, [fetchedRoles]);

  // Escape key to close
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  });

  // Cleanup timers
  useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      if (fadeTimerRef.current) clearTimeout(fadeTimerRef.current);
    };
  }, []);

  const debouncedSave = useCallback((rolesToSave: RoleDefinition[]) => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    if (fadeTimerRef.current) clearTimeout(fadeTimerRef.current);
    pendingRolesRef.current = rolesToSave;

    saveTimerRef.current = setTimeout(async () => {
      setSaveStatus("saving");
      try {
        await api.saveRoles({ roles: rolesToSave });
        pendingRolesRef.current = null;
        setSaveStatus("saved");
        fadeTimerRef.current = setTimeout(() => setSaveStatus("idle"), 2000);
      } catch (err) {
        console.error("Failed to save roles:", err);
        setSaveStatus("idle");
      }
    }, 800);
  }, []);

  const handleClose = useCallback(() => {
    if (saveTimerRef.current && pendingRolesRef.current) {
      clearTimeout(saveTimerRef.current);
      api.saveRoles({ roles: pendingRolesRef.current }).catch(console.error);
    }
    onClose();
  }, [onClose]);

  const handleFieldChange = useCallback(
    (field: keyof RoleDefinition, value: string | boolean) => {
      setRoles((prev) => {
        const updated = prev.map((r) => (r.id === selectedId ? { ...r, [field]: value } : r));
        debouncedSave(updated);
        return updated;
      });
      if (field === "id") setSelectedId(value as string);
    },
    [selectedId, debouncedSave],
  );

  const handleAddRole = useCallback(() => {
    const newRole = makeDefaultRole();
    setRoles((prev) => {
      const updated = [...prev, newRole];
      debouncedSave(updated);
      return updated;
    });
    setSelectedId(newRole.id);
  }, [debouncedSave]);

  const handleDeleteRole = useCallback(() => {
    if (!selectedRole) return;
    if (!confirm(`Delete role "${selectedRole.label}"?`)) return;
    setRoles((prev) => {
      const updated = prev.filter((r) => r.id !== selectedId);
      debouncedSave(updated);
      setSelectedId(updated[0]?.id ?? null);
      return updated;
    });
  }, [selectedId, selectedRole, debouncedSave]);

  const handleMoveRole = useCallback(
    (roleId: string, direction: "up" | "down") => {
      setRoles((prev) => {
        const idx = prev.findIndex((r) => r.id === roleId);
        if (idx < 0) return prev;
        const newIdx = direction === "up" ? idx - 1 : idx + 1;
        if (newIdx < 0 || newIdx >= prev.length) return prev;
        const updated = [...prev];
        [updated[idx], updated[newIdx]] = [updated[newIdx], updated[idx]];
        debouncedSave(updated);
        return updated;
      });
    },
    [debouncedSave],
  );

  const handleDuplicateRole = useCallback(() => {
    if (!selectedRole) return;
    const duplicate: RoleDefinition = {
      ...selectedRole,
      id: `role-${nanoid(6)}`,
      label: `${selectedRole.label} (copy)`,
    };
    setRoles((prev) => {
      const idx = prev.findIndex((r) => r.id === selectedId);
      const updated = [...prev];
      updated.splice(idx + 1, 0, duplicate);
      debouncedSave(updated);
      return updated;
    });
    setSelectedId(duplicate.id);
  }, [selectedRole, selectedId, debouncedSave]);

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      onClick={(e) => {
        if (e.target === e.currentTarget) handleClose();
      }}
    >
      <div className="bg-white dark:bg-zinc-900 rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-zinc-200 dark:border-zinc-800">
          <h2 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">Edit Roles</h2>
          <div className="flex items-center gap-3">
            {saveStatus === "saving" && (
              <span className="text-xs text-zinc-400 animate-pulse">Saving...</span>
            )}
            {saveStatus === "saved" && (
              <span className="text-xs text-green-600 dark:text-green-400">Saved &#10003;</span>
            )}
            <button
              onClick={handleClose}
              className="text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Body */}
        {loading ? (
          <div className="p-8 text-center text-zinc-400">Loading...</div>
        ) : (
          <div className="flex flex-1 min-h-0 overflow-hidden">
            {/* Left panel: role list */}
            <div className="w-56 border-r border-zinc-200 dark:border-zinc-700 flex flex-col">
              <div className="flex-1 overflow-y-auto py-2">
                {roles.map((role, idx) => (
                  <div
                    key={role.id}
                    className={`flex items-center transition-colors ${
                      selectedId === role.id
                        ? "bg-blue-50 dark:bg-blue-900/20 border-r-2 border-blue-500"
                        : "hover:bg-zinc-50 dark:hover:bg-zinc-800"
                    }`}
                  >
                    <button
                      onClick={() => setSelectedId(role.id)}
                      className="flex-1 text-left px-4 py-2.5 min-w-0"
                    >
                      <div className="font-medium text-sm text-zinc-900 dark:text-zinc-100 truncate">
                        {role.label}
                      </div>
                      <div className="text-xs text-zinc-500 truncate">{role.description}</div>
                    </button>
                    <div className="flex flex-col pr-1.5 shrink-0">
                      <button
                        onClick={() => handleMoveRole(role.id, "up")}
                        disabled={idx === 0}
                        className="p-0.5 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200 disabled:opacity-20 disabled:cursor-default"
                      >
                        <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleMoveRole(role.id, "down")}
                        disabled={idx === roles.length - 1}
                        className="p-0.5 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200 disabled:opacity-20 disabled:cursor-default"
                      >
                        <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              <div className="p-3 border-t border-zinc-200 dark:border-zinc-700">
                <button
                  onClick={handleAddRole}
                  className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 text-sm rounded-md transition-colors bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-700 text-zinc-700 dark:text-zinc-300"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 4v16m8-8H4"
                    />
                  </svg>
                  Add Role
                </button>
              </div>
            </div>

            {/* Right panel: edit form */}
            <div className="flex-1 overflow-y-auto p-6">
              {selectedRole ? (
                <RoleForm
                  role={selectedRole}
                  onChange={handleFieldChange}
                  onDelete={handleDeleteRole}
                  onDuplicate={handleDuplicateRole}
                />
              ) : (
                <div className="text-sm text-zinc-400 text-center mt-12">
                  Select a role to edit
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
